import {
  adopt,
  claimStart,
  dispatch,
  getAttempt,
  isNewerThanSeen,
  nextAttemptId,
  noteObservedRequestedAt,
  registerTeardown,
  registerWatcher,
  releaseStart,
  runSerially,
  scopedKey,
  setOptimistic,
} from "./store";
import type { ActionIdentity, Attempt } from "./types";
import { adoptedAttempt } from "./machine";

/**
 * What the read model currently says about a key. This is the ONE thing an
 * action has to teach the layer about its feature; everything else is generic.
 */
export interface Observation {
  /** Identity of the run the read model is showing, when it has one. */
  runId?: string;
  /**
   * When that run was requested. Used to tell "this is the run I just started"
   * from "this is the run that finished last week" in the one case where there
   * is no run id to match on — an attempt whose response was lost (§4.9).
   */
  requestedAt?: string | null;
  status: "none" | "pending" | "succeeded" | "failed" | "canceled";
  error?: string | null;
}

/**
 * One query entry an action's result shows up in — procedure AND input.
 *
 * Never a procedure alone. A cache key is `[procedure, input]`, so
 * `getRoundVideos` names a family: round A and round B are different entries.
 * A family name can neither fence (round B's unrelated refetch would confirm
 * an action taken on round A) nor be re-observed once the originating page has
 * unmounted, which is exactly when the layer needs to construct the read
 * itself (§4.4).
 */
export interface ConfirmQuery {
  /**
   * Abort any fetch already in flight for this entry. React Query DEDUPES: a
   * read issued while an earlier fetch is running attaches to that fetch,
   * whose response was computed BEFORE the mutation landed — so without this,
   * pre-mutation data is accepted as proof of the mutation.
   */
  cancel: () => Promise<void>;
  /** Force a network read of this exact entry. Must pass `{ staleTime: 0 }`. */
  fetch: () => Promise<unknown>;
}

/**
 * The tRPC utils shape for one procedure — `utils.getRoundVideos` and friends.
 * Structural rather than imported so the layer stays independent of the router.
 */
interface QueryUtil<TQueryInput> {
  // Method syntax, not property syntax, on purpose: property-style function
  // types are checked contravariantly under `strictFunctionTypes`, and tRPC's
  // `fetch` declares a specific options type that `unknown` is not a supertype
  // of. Methods are checked bivariantly, which is what lets any procedure's
  // utils object satisfy this shape.
  cancel(input: TQueryInput): Promise<unknown>;
  fetch(input: TQueryInput, opts?: unknown): Promise<unknown>;
}

/**
 * Declare one query entry an action confirms against.
 *
 * Exists so no call site has to remember the two rules that make confirmation
 * sound: cancel before fetching (React Query would otherwise dedupe the
 * confirming read onto a fetch that started before the mutation), and force the
 * network (the app's one-minute `staleTime` would otherwise answer from the
 * pre-mutation cache). Both were easy to get wrong and silent when wrong.
 */
export function confirmQuery<TQueryInput>(
  util: QueryUtil<TQueryInput>,
  input: TQueryInput,
): ConfirmQuery {
  return {
    cancel: async () => void (await util.cancel(input)),
    fetch: () => util.fetch(input, { staleTime: 0 }),
  };
}

interface ActionBase<TInput> {
  name: string;
  key: (input: TInput) => string;
  /** Enqueue the work. Resolves with whatever the trigger route returns. */
  run: (input: TInput) => Promise<unknown>;
  /**
   * How this client behaves when the same key is pressed while an attempt is
   * still outstanding. Defaults to `"drop"`.
   *
   * There is deliberately no `"replace"`. `replace` lets one client race
   * itself, and the loser is whichever request COMMITS last — something the
   * client cannot observe, let alone fix. It is only sound against an endpoint
   * that accepts a server-issued fencing token and refuses a write based on a
   * stale revision (§4.8), and no endpoint in this app offers one. Leaving it
   * out of the type means it can't be declared by mistake.
   */
  concurrency?: "drop" | "serialize";
  /**
   * What to show while the attempt is outstanding. Held BY THE STORE and
   * merged over server truth at render — never written into the query cache.
   * Read it with `useActionOptimistic`.
   *
   * Assert only what the user supplied: the text they typed, the row they
   * picked. Never a server-derived value (`updatedAt`, counts, ranks, a
   * position in a server-sorted list) — show the previous value or a neutral
   * placeholder instead. A wrong number that quietly corrects itself is the
   * same betrayal as a flashing button (§4.7).
   */
  optimistic?: (input: TInput) => unknown;
  /**
   * Optional shared admission gate (e.g. the Videos page's Cloud Tasks
   * limiter). Returning `null` means the gate was held and nothing ran — which
   * is not a failure, so the attempt is discarded rather than marked failed.
   */
  admit?: <T>(fn: () => Promise<T>) => Promise<T | null>;
  /** Flip presentation to "taking longer than usual". Never changes state. */
  slowAfterMs?: number;
  onError?: (message: string) => void;
}

/**
 * Confirmed by server run state, fenced on run identity. For worker-backed
 * work — anything with an `AsyncEnvelope` or a run row behind it.
 */
interface RunsConfirmation<TInput> {
  confirm: "runs";
  /**
   * Pull the run identity out of the trigger's result. Return `null` when the
   * server queued nothing because the result was already current — there is no
   * run to watch, and the attempt is done.
   */
  identify: (result: unknown) => ActionIdentity | null;
  /**
   * Fetch the query the feature actually renders. Owned by the attempt, so it
   * keeps working with no relevant page mounted — `utils.x.fetch(input)`, not
   * `invalidate`, which no-ops when nothing is observing.
   *
   * MUST force a network round-trip: pass `{ staleTime: 0 }`. The app sets a
   * one-minute global `staleTime`, so a plain `fetch` hands back the cached
   * PRE-mutation envelope, whose run id is the previous run's — and confirming
   * an attempt against the run it just replaced is exactly the bug the
   * identity fencing exists to prevent.
   */
  fetch: (input: TInput) => Promise<unknown>;
  /**
   * Read this target's current state out of what `fetch` returned. Receives
   * the original input rather than the store key, because the store key is
   * namespaced (round, track) and the read model is not.
   */
  inspect: (data: unknown, input: TInput) => Observation | null;
  /**
   * How long the read model may show nothing about our run before we stop
   * tracking it. Absence PLUS time is evidence; absence alone never is, and a
   * record we can still see is alive however long it takes.
   */
  releaseAfterMissingMs?: number;
  /**
   * What a server CONFLICT means for this action. The default is "someone else
   * already enqueued exactly this run" — adopt theirs from the read model,
   * because two admins pressing Regenerate is ordinary and a conflict error is
   * something neither of them can act on. Set `false` where a CONFLICT is a
   * genuine rejection ("state changed; refresh and retry"), so it surfaces
   * instead of spending a few polls and then releasing in silence.
   *
   * Only on the RUNS variant: a `queries` action has no run to adopt, so it
   * never takes the adoption path at all. That used to need remembering on
   * every run-less action; now the strategy says it.
   */
  adoptOnConflict?: boolean;
}

/**
 * Confirmed by the action's own reads. The default for ordinary mutations —
 * saving a name, moving a row, reordering a list — which have no run row and
 * therefore no server identity to fence on.
 *
 * Fencing is by request ORDERING instead: the attempt leaves `applying` once
 * it has completed a read that provably started after the mutation resolved.
 * The layer owns that read (it cancels first, then fetches) so a call site
 * cannot accidentally accept a deduped pre-mutation response as proof.
 */
interface QueriesConfirmation<TInput> {
  confirm: "queries";
  /**
   * The exact query entries this action's result becomes visible in. Owned by
   * the attempt, so confirmation still happens with no page mounted —
   * `invalidate` would mark a query stale and never fetch it, and the attempt
   * would wait forever for a request nobody was going to make (§4.6).
   */
  confirmWith: (input: TInput) => readonly ConfirmQuery[];
}

export type RunsActionDefinition<TInput> = ActionBase<TInput> &
  RunsConfirmation<TInput>;
export type QueriesActionDefinition<TInput> = ActionBase<TInput> &
  QueriesConfirmation<TInput>;
export type ActionDefinition<TInput> =
  | RunsActionDefinition<TInput>
  | QueriesActionDefinition<TInput>;

const DEFAULT_SLOW_MS = 120_000;
const DEFAULT_RELEASE_AFTER_MISSING_MS = 60_000;

/**
 * Does this error mean the server said no, or that we never found out?
 *
 * The distinction decides between `failed` (safe to retry) and `unknown` (may
 * already have applied). A tRPC error carrying a server-shaped payload is an
 * answer; a bare network/abort error is silence.
 */
function classifyError(error: unknown): { rejected: boolean; message: string } {
  const message =
    error instanceof Error ? error.message : "Something went wrong.";
  const data = (error as { data?: { code?: string } } | null)?.data;
  const code = data?.code;
  if (typeof code === "string" && code.length > 0) {
    return { rejected: true, message };
  }
  return { rejected: false, message };
}

function isConflict(error: unknown): boolean {
  const code = (error as { data?: { code?: string } } | null)?.data?.code;
  return code === "CONFLICT";
}

// Overloaded so the strategy survives the call: the helpers below
// (`attachWatcher`, `reconcileUnknown`, `adoptInFlight`) only make sense for a
// `runs` action, and a signature that returned the bare union would hand every
// caller a type those helpers reject.
export function defineAction<TInput>(
  definition: RunsActionDefinition<TInput>,
): RunsActionDefinition<TInput>;
export function defineAction<TInput>(
  definition: QueriesActionDefinition<TInput>,
): QueriesActionDefinition<TInput>;
export function defineAction<TInput>(
  definition: ActionDefinition<TInput>,
): ActionDefinition<TInput> {
  return definition;
}

export function actionKey<TInput>(
  definition: ActionDefinition<TInput>,
  input: TInput,
): string {
  return scopedKey(definition.name, definition.key(input));
}

/**
 * Attach the poll that confirms an attempt. Exported because a caller can own
 * the enqueue itself (the Videos list awaits it inside a shared rate-limit
 * gate and reads per-video outcomes off the result) and still needs the
 * attempt watched afterwards.
 */
export function attachWatcher<TInput>(
  definition: RunsActionDefinition<TInput>,
  input: TInput,
  key: string,
): void {
  const missingSince = { at: null as number | null };
  const releaseAfter =
    definition.releaseAfterMissingMs ?? DEFAULT_RELEASE_AFTER_MISSING_MS;

  registerWatcher(key, {
    refresh: async () => {
      const attempt = getAttempt(key);
      if (!attempt) return;

      const data = await definition.fetch(input);
      const observed = definition.inspect(data, input);
      // Record what the server says exists, so a later reconciliation can tell
      // a new run from one that predates the press.
      noteObservedRequestedAt(key, observed?.requestedAt);
      const current = getAttempt(key);
      if (!current || current.attemptId !== attempt.attemptId) return;

      const ourRunId = current.identity?.runId;

      if (!observed || observed.status === "none") {
        // Absence is not evidence — but absence that persists past the point a
        // record should have appeared is. Until then, keep waiting.
        missingSince.at ??= Date.now();
        if (Date.now() - missingSince.at > releaseAfter) {
          dispatch(key, { type: "RELEASED" });
        }
        return;
      }
      if (ourRunId && observed.runId && observed.runId !== ourRunId) {
        // Not ours. Per §4.3 this reads as PENDING, never as a result — the
        // record we can see says nothing about the run we're waiting on.
        //
        // It deliberately does NOT release on sight. An earlier version did,
        // and a stale cache read was enough to kill a healthy attempt two
        // seconds after the click: the page still held the previous run's
        // COMPLETED envelope, the id didn't match, and the spinner vanished
        // back to the old result. Unmatched instead counts toward the same
        // absence-plus-time budget as a missing record, so a genuinely
        // superseded run still lets go — just on evidence rather than on the
        // first disagreeing read.
        missingSince.at ??= Date.now();
        if (Date.now() - missingSince.at > releaseAfter) {
          dispatch(key, { type: "RELEASED" });
        }
        return;
      }

      missingSince.at = null;

      if (observed.status === "pending") return;

      if (observed.status === "succeeded") {
        dispatch(key, {
          type: "CONTROL_TERMINAL",
          attemptId: current.attemptId,
          terminal: "SUCCEEDED",
        });
        // This feature's control plane and display query are the same payload,
        // so the artifact is in hand the moment the run reports success. Both
        // phases still get dispatched: when a feature splits them onto separate
        // queries, only this second call moves.
        dispatch(key, {
          type: "DISPLAY_CONFIRMED",
          attemptId: current.attemptId,
        });
        return;
      }

      dispatch(key, {
        type: "CONTROL_TERMINAL",
        attemptId: current.attemptId,
        terminal: observed.status === "failed" ? "FAILED" : "CANCELED",
        error: observed.error ?? undefined,
      });
    },
  });

  const slowAfter = definition.slowAfterMs ?? DEFAULT_SLOW_MS;
  const attemptId = getAttempt(key)?.attemptId;
  const timer = setTimeout(() => {
    if (attemptId == null) return;
    dispatch(key, { type: "SLOW", attemptId });
  }, slowAfter);
  registerTeardown(key, () => clearTimeout(timer));
}

/**
 * What a press did, for callers that sequence on it.
 *
 * `run()` deliberately does NOT reject. Most presses are fire-and-forget
 * (`void action.run(...)`), and a rejecting promise would turn every one of
 * those into an unhandled rejection. Callers that need the outcome — a dialog
 * that closes on success, a chain whose later steps are only valid if the
 * earlier ones landed — read it from here instead.
 */
export interface ActionOutcome {
  /** True when the write reached the server and was accepted. */
  ok: boolean;
  reason:
    | null
    /** The server refused it. Nothing applied; retrying is safe. */
    | "failed"
    /** We never learned the outcome. It may have applied. */
    | "unknown"
    /**
     * Another attempt for this key was already in flight. Normal — it is what
     * a double-click looks like — and deliberately silent, because the control
     * is already showing that press as pending.
     */
    | "dropped"
    /**
     * An admission gate refused it, so nothing was sent. Distinct from
     * `dropped` because the control looked available and the user's press did
     * nothing: this one has to be surfaced, or the click is silently lost.
     */
    | "gated";
  error: string | null;
}

const OK: ActionOutcome = { ok: true, reason: null, error: null };

function notOk(
  reason: NonNullable<ActionOutcome["reason"]>,
  error: string | null = null,
): ActionOutcome {
  return { ok: false, reason, error };
}

/**
 * Start (or restart) an action. Safe to call from anywhere — it is the store,
 * not the caller's component, that owns everything the attempt needs.
 */
export async function runAction<TInput>(
  definition: ActionDefinition<TInput>,
  input: TInput,
): Promise<ActionOutcome> {
  const key = actionKey(definition, input);

  if (definition.concurrency === "serialize") {
    // The queue has to wrap the whole press, not sit inside it. The start
    // claim below is held for as long as the request is in flight, so a second
    // press that waited to take the claim would be turned away before it ever
    // reached a queue.
    // Only a press that ACTUALLY waited counts as the queue releasing. An
    // unconditional `true` here treated a fresh retry as a queued one, so a
    // serialized action — rename, and both text editors — stayed wedged after
    // an unknown outcome even though pressing again is exactly how the user
    // is meant to get out of it.
    return (
      (await runSerially(key, (waited) =>
        claimAndStart(definition, input, key, waited),
      )) ?? notOk("dropped")
    );
  }
  return claimAndStart(definition, input, key, false);
}

async function claimAndStart<TInput>(
  definition: ActionDefinition<TInput>,
  input: TInput,
  key: string,
  queued: boolean,
): Promise<ActionOutcome> {
  // Claimed before any await. Everything below yields at some point, and the
  // attempt status does not change while a reconciliation fetch is in flight.
  if (!claimStart(key)) return notOk("dropped");
  try {
    return await start(definition, input, key, queued);
  } finally {
    releaseStart(key);
  }
}

async function start<TInput>(
  definition: ActionDefinition<TInput>,
  input: TInput,
  key: string,
  queued: boolean,
): Promise<ActionOutcome> {
  const existing = getAttempt(key);
  if (
    existing &&
    (existing.status === "sent" || existing.status === "applying")
  ) {
    // Debounce, never a lock: repeat clicks while genuinely in flight are
    // ignored, and any attempt that is no longer pending can be restarted.
    //
    // A `serialize` action does not normally reach here — its queue means the
    // previous press has already settled by the time this one runs — but an
    // attempt adopted from elsewhere can still be outstanding, and dropping
    // the press is the right answer for that too.
    return notOk("dropped");
  }

  // An `unknown` attempt means a previous press whose outcome we never saw —
  // the server may well have accepted it. Reconcile BEFORE spending again.
  //
  // This lives here rather than in each adapter on purpose: it was written by
  // hand three times (summaries controller, Videos list, video detail) and got
  // a different shape each time, twice leaving a way to double-trigger and once
  // leaving the control with no way out at all. One implementation, one
  // behaviour, every caller.
  if (existing?.status === "unknown") {
    if (queued) {
      // A press that waited behind the unknown one — the QUEUE releasing, not
      // the user asking again. §4.8: the queue must not release past an
      // unknown outcome, because the orphaned earlier write may still be in
      // flight and would land on top of this one.
      return notOk("unknown", existing.error);
    }
    if (definition.confirm === "queries") {
      // An explicit press. The machine allows TRIGGER from `unknown`
      // (MACHINE.md note 5) precisely because retrying is the USER's decision,
      // made from a control that has already said "this may have applied" —
      // which is what `isIndeterminate` is for.
      //
      // An earlier version returned here instead. That read as prudent and was
      // worse: with no run to reconcile against, the key had no exit at all,
      // so one dropped connection left that control dead until a reload.
      dispatch(key, { type: "ACKNOWLEDGE" });
    } else {
      const resolved = await reconcileUnknown(definition, input, key, existing);
      if (resolved) {
        return getAttempt(key)?.status === "succeeded" ? OK : notOk("unknown");
      }
    }
  }

  dispatch(key, { type: "TRIGGER", at: Date.now() });
  const attemptId = getAttempt(key)?.attemptId;
  if (attemptId == null) return notOk("dropped");

  // Registered AFTER the TRIGGER, because a TRIGGER over an existing attempt
  // releases that attempt's resources — including its optimistic value. Doing
  // this first would hand the new value to the old attempt's teardown.
  if (definition.optimistic) {
    setOptimistic(key, attemptId, definition.optimistic(input));
  }

  const invoke = async () => definition.run(input);
  let result: unknown;
  try {
    result = definition.admit ? await definition.admit(invoke) : await invoke();
  } catch (error) {
    if (
      definition.confirm === "runs" &&
      isConflict(error) &&
      (definition.adoptOnConflict ?? true)
    ) {
      // Someone else is already doing exactly this. Adopt their run rather
      // than reporting a conflict the user can do nothing with — two admins
      // pressing Regenerate is ordinary. The identity comes from the read
      // model, because the conflict response doesn't carry one.
      await adoptFromReadModel(definition, input, key, attemptId);
      // Someone else's run is doing this work; from the caller's point of view
      // the thing they asked for is happening.
      return OK;
    }
    const { rejected, message } = classifyError(error);
    dispatch(key, {
      type: rejected ? "REJECTED" : "INDETERMINATE",
      attemptId,
      error: message,
    });
    definition.onError?.(message);
    // The optimistic value is gone with the attempt, so the view has just
    // re-derived from whatever truth it still holds — which may itself be
    // stale, since the write may have partly landed. Settle it authoritatively
    // rather than leaving the user to guess (§4.8).
    if (definition.confirm === "queries" && definition.optimistic) {
      await refreshConfirmTargets(definition, input);
    }
    return notOk(rejected ? "failed" : "unknown", message);
  }

  if (result === null) {
    // The admission gate was held; nothing was enqueued and nothing happened.
    dispatch(key, { type: "RELEASED" });
    return notOk("gated");
  }

  if (definition.confirm === "queries") {
    // The mutation has resolved, so the server has applied it. What the user
    // is looking at has not caught up yet — that gap is `applying`, and it
    // ends when a read that STARTED after this moment comes back.
    dispatch(key, { type: "ACCEPTED", attemptId, identity: {} });
    await confirmFromQueries(definition, input, key, attemptId);
    // The write committed — the mutation resolved. Whether the confirming read
    // then succeeded is a question about what is on screen, not about whether
    // the caller's change happened, so it does not change the reported outcome.
    return OK;
  }

  const identity = definition.identify(result);
  if (!identity) {
    dispatch(key, { type: "ACCEPTED_SETTLED", attemptId });
    return OK;
  }

  dispatch(key, { type: "ACCEPTED", attemptId, identity });
  attachWatcher(definition, input, key);
  return OK;
}

/**
 * Confirm a `queries` attempt: cancel whatever is in flight for each declared
 * entry, then read it fresh. Both halves matter — the cancel is what stops
 * React Query handing back a response computed before the mutation landed.
 */
async function confirmFromQueries<TInput>(
  definition: QueriesActionDefinition<TInput>,
  input: TInput,
  key: string,
  attemptId: number,
): Promise<void> {
  try {
    await Promise.all(
      definition.confirmWith(input).map(async (target) => {
        await target.cancel();
        await target.fetch();
      }),
    );
  } catch {
    // The write is known-committed — the mutation resolved. Failing to LOOK is
    // not a failure of the action, so this is a release rather than an error
    // (MACHINE.md note 3): drop the optimistic value and let the page's own
    // reads render whatever truth they next see.
    dispatch(key, { type: "RELEASED" });
    return;
  }
  dispatch(key, { type: "DISPLAY_CONFIRMED", attemptId });
}

/** Authoritative re-read, used after a failure that may have half-applied. */
async function refreshConfirmTargets<TInput>(
  definition: QueriesActionDefinition<TInput>,
  input: TInput,
): Promise<void> {
  await Promise.all(
    definition.confirmWith(input).map(async (target) => {
      await target.cancel().catch(() => undefined);
      await target.fetch().catch(() => undefined);
    }),
  );
}

/**
 * Try to settle an `unknown` attempt from evidence. Returns true when the lost
 * press turned out to have landed, so the caller must NOT trigger again.
 */
export async function reconcileUnknown<TInput>(
  definition: RunsActionDefinition<TInput>,
  input: TInput,
  key: string,
  attempt: Attempt,
): Promise<boolean> {
  const data = await definition.fetch(input).catch(() => undefined);
  // If impersonation changed while we were awaiting, this result belongs to
  // the previous viewer. Installing it now would put their run under the new
  // viewer's scope and poll it as them, straight past the viewer reset.
  if (actionKey(definition, input) !== key) return true;
  if (data === undefined) {
    // We couldn't even look. Refuse to guess in either direction: leaving it
    // `unknown` keeps the uncertainty visible rather than silently spending.
    return true;
  }
  const observed = definition.inspect(data, input);

  if (observed?.status === "pending") {
    dispatch(key, { type: "ACKNOWLEDGE" });
    if (observed.runId) adoptInFlight(definition, input, observed);
    return true;
  }

  // Only a run NEWER than anything that existed before the press says anything
  // about our press. An older completed artifact is simply what was already
  // there, and treating it as proof would consume the retry and claim work that
  // never happened.
  //
  // Compared server-timestamp to server-timestamp, never against the browser
  // clock: a machine running a few seconds fast would otherwise judge a
  // genuinely-new run as old, reject valid evidence, and enqueue a duplicate
  // paid run. Two clocks are not one clock.
  if (
    observed?.status === "succeeded" &&
    isNewerThanSeen(key, observed.requestedAt)
  ) {
    dispatch(key, {
      type: "EVIDENCE_APPLIED",
      attemptId: attempt.attemptId,
    });
    return true;
  }

  // Nothing found: the press really didn't land, so retrying is safe. Drop the
  // unknown attempt so the re-trigger below starts clean.
  dispatch(key, { type: "ACKNOWLEDGE" });
  return false;
}

async function adoptFromReadModel<TInput>(
  definition: RunsActionDefinition<TInput>,
  input: TInput,
  key: string,
  attemptId: number,
): Promise<void> {
  // The winner may not have written its envelope yet: it can throw CONFLICT at
  // us before its own cache refresh lands. One look is therefore not enough —
  // giving up after it releases the attempt, and on a page with no interval of
  // its own (Recommender Summaries) nothing then adopts or polls the winning
  // run, so the control re-enables over work that is genuinely starting.
  const ATTEMPTS = 5;
  for (let i = 0; i < ATTEMPTS; i += 1) {
    if (i > 0) {
      await new Promise((resolve) => setTimeout(resolve, 400 * i));
    }
    // A viewer switch during these awaits must not install one viewer's run
    // under another's scope — the key is recomputed from the CURRENT viewer.
    if (actionKey(definition, input) !== key) return;

    try {
      const data = await definition.fetch(input);
      if (actionKey(definition, input) !== key) return;
      const observed = definition.inspect(data, input);
      if (observed?.status === "pending" && observed.runId) {
        dispatch(key, {
          type: "ACCEPTED",
          attemptId,
          identity: { runId: observed.runId },
        });
        attachWatcher(definition, input, key);
        return;
      }
      // A terminal result is also an answer: the competing run finished while
      // we were looking, and the read model now holds it.
      if (
        observed?.status === "succeeded" ||
        observed?.status === "failed" ||
        observed?.status === "canceled"
      ) {
        break;
      }
    } catch {
      // Keep trying — a failed look is not an answer.
    }
  }
  // Nothing adoptable — drop the attempt and let server truth render. Better
  // than a "Conflict" error for something that is not the user's problem.
  if (actionKey(definition, input) === key) {
    dispatch(key, { type: "RELEASED" });
  }
}

/**
 * Hand the layer what the read model currently says about a target.
 *
 * Adopts in-flight work this client didn't start — a previous page load,
 * another tab, another admin — and records the server timestamp for every
 * observation, running or finished, so reconciliation has a baseline.
 *
 * Idempotent and safe for ANY status: call it for every row whenever a scope
 * comes into view rather than filtering to pending first.
 */
export function adoptInFlight<TInput>(
  definition: RunsActionDefinition<TInput>,
  input: TInput,
  observed: Observation,
): void {
  const key = actionKey(definition, input);
  // Seed the baseline from whatever the page is ALREADY showing, including a
  // completed artifact. Without this the common case has no baseline at all —
  // open a page with a finished summary, regenerate, lose the response, and
  // reconciliation would refuse to recognise the new completed run purely
  // because it had nothing to compare against, then enqueue a duplicate.
  noteObservedRequestedAt(key, observed.requestedAt);
  if (observed.status !== "pending" || !observed.runId) return;
  const attempt: Attempt = adoptedAttempt(
    // From the same per-key source as triggers — see `nextAttemptId`.
    nextAttemptId(key),
    { runId: observed.runId },
    Date.now(),
  );
  if (adopt(key, attempt)) attachWatcher(definition, input, key);
}
